/**
 * Cliente del módulo de Onboarding.
 *
 * POR QUÉ NO USA `utils/axios` (el cliente global del panel)
 * ---------------------------------------------------------
 * Ese cliente tiene un interceptor que manda al login CUALQUIER 403:
 *
 *     if (error?.response?.status === 403 && !pathname.startsWith('/auth/')) {
 *       window.location = `/auth/login?...`;
 *     }
 *
 * Y en estas rutas un 403 significa "estás autenticado pero no perteneces al
 * grupo de revisores". Con el cliente global, un usuario del panel sin ese
 * permiso saldría expulsado a la pantalla de login: parecería un problema de
 * sesión en lugar de una falta de permiso.
 *
 * Se usa el MISMO baseURL y el MISMO access token de Cognito: no hay login
 * aparte ni credencial nueva. Lo único que cambia es que este cliente no
 * reinterpreta el 403.
 */

import axios from 'axios';
import { stringify } from 'querystring';

import { API_URL } from 'settings';
import { getCurrentSessionData } from 'utils/aws/cognito';
import { toBase64Utf8 } from 'utils/strings';

const BASE = '/onboarding/admin/';

/**
 * Para probar en local se puede apuntar SOLO este módulo al gateway local
 * (`REACT_APP_ONBOARDING_API_URL=http://127.0.0.1:5050/`), dejando el resto
 * del panel contra el backend de siempre. Sin esa variable usa el mismo
 * API_URL que todo lo demás.
 *
 * Solo se respeta con `npm start`. Un build de producción salió con esa
 * variable en su entorno y el menú, al consultar `access` en cada página,
 * hacía que Chrome pidiera permiso para entrar a la red local del usuario. En
 * `npm run build` se ignora y siempre se usa API_URL.
 */
const ONBOARDING_API_URL = (process.env.NODE_ENV === 'development' && process.env.REACT_APP_ONBOARDING_API_URL)
  || API_URL;

const http = axios.create({ baseURL: ONBOARDING_API_URL });

http.interceptors.request.use((config) => {
  const { accessToken } = getCurrentSessionData();
  // El panel manda el access token crudo (sin "Bearer"), que es el que trae
  // la lista `cognito:groups` que el backend verifica.
  config.headers.Authorization = accessToken;
  return config;
});

/**
 * Sesión vencida (401): al login, y de vuelta a la misma página al entrar,
 * igual que el resto del panel. El panel no refresca el token en ningún módulo.
 *
 * Menos la consulta de `access` que hace el menú al cargar: si fallara para un
 * usuario cuyo token el gateway no acepta, lo mandaría al login en cada página.
 * Esa consulta, si falla, solo oculta el menú.
 */
http.interceptors.response.use(
  (response) => response,
  (error) => {
    const { hash = '', pathname, search = '' } = window.location;
    if (error?.response?.status === 401 && !error.config?.skipLoginRedirect && !pathname.startsWith('/auth/')) {
      window.location = `/auth/login?${stringify({ redirect: toBase64Utf8(`${pathname}${search}${hash}`) })}`;
      // La página se está descargando: nadie debe procesar esta respuesta.
      return new Promise(() => {});
    }
    return Promise.reject(error);
  },
);

/**
 * Estados del expediente (public.taxonomy, grupo 36).
 * `chip` es el color que espera el componente Label del panel.
 */
export const STATUS = {
  // `chip` sale de los 5 colores que admite components/Label: primary,
  // secondary, error, warning y success. No hay 'info' ni 'default'.
  1: { label: 'Borrador', chip: 'secondary' },
  2: { label: 'Enviado', chip: 'primary' },
  3: { label: 'En revisión', chip: 'warning' },
  4: { label: 'Corrección solicitada', chip: 'warning' },
  5: { label: 'Reenviado', chip: 'primary' },
  6: { label: 'Aprobado', chip: 'success' },
  7: { label: 'Rechazado', chip: 'error' },
};

/** Estados en los que el revisor puede tomar, aprobar o pedir corrección. */
export const ACTIONABLE = [2, 3, 5];

/**
 * Rechazar admite además el 4: es el único camino para cerrar un expediente
 * cuya corrección el cliente nunca atendió. Desde el 4 ninguna otra acción
 * del revisor aplica, porque la pelota es del cliente.
 */
export const REJECTABLE = [2, 3, 4, 5];

/** A partir de cuántos días sin movimiento se marca un expediente. */
export const IDLE_WARNING_DAYS = 7;

export const PERSON_TYPE = { fisica: 'Persona Física', moral: 'Persona Moral' };

/**
 * Ronda que se muestra: la que se está revisando o la última que se revisó.
 * `review_round` cuenta las correcciones pedidas: esperando al cliente (4) es
 * la ronda de esa corrección; en cualquier otro estado, la siguiente.
 */
export function roundOf(row) {
  const done = Number(row?.review_round) || 0;
  return row?.status === 4 ? Math.max(done, 1) : done + 1;
}

/** Etiquetas de los campos del checklist, para agrupar los documentos. */
export const FIELD_LABELS = {
  identificacion_oficial: 'Identificación oficial',
  constancia_fiscal: 'Constancia de situación fiscal',
  constancia_fiscal_representante: 'Constancia fiscal del representante',
  caratula_estado_cuenta: 'Carátula de estado de cuenta',
  comprobante_domicilio: 'Comprobante de domicilio',
  fotos_comercio: 'Fotos del comercio',
  acta_constitutiva: 'Acta constitutiva y poderes',
  id_representante_legal: 'Identificación del representante legal',
};

async function call(op, payload = {}, options = {}) {
  const response = await http.post(`${BASE}${op}`, payload, options);
  return response.data;
}

// Respuesta de `access`, una sola vez por carga del panel.
let accessPromise = null;

/**
 * Marcas que el usuario puede revisar: `{ all, tenants }`, o `null` si no es
 * revisor (o no se pudo saber). Lo decide el gateway con sus grupos de Cognito;
 * el panel solo lo pregunta. Se guarda por carga: el menú y la cola lo usan.
 */
export function onboardingAccess() {
  if (!accessPromise) {
    accessPromise = call('access', {}, { skipLoginRedirect: true })
      .then((data) => (data && Array.isArray(data.tenants) && data.tenants.length
        ? { all: Boolean(data.all), tenants: data.tenants }
        : null))
      .catch(() => null);
  }
  return accessPromise;
}

export const Onboarding = {
  /** Cola de expedientes. Devuelve { data, total }. */
  list: (filters) => call('list', filters),
  detail: (idUuid) => call('detail', { id_uuid: idUuid }),
  /** URL de descarga de 60 s. El backend valida que la key sea del expediente. */
  download: (idUuid, key) => call('download', { id_uuid: idUuid, key }),
  /** Igual, pero `inline`: para mostrar el documento dentro del panel. */
  view: (idUuid, key) => call('download', { id_uuid: idUuid, key, inline: true }),
  /** CLABE en claro. Acto explícito: no viaja en `detail`. */
  clabe: (idUuid) => call('clabe', { id_uuid: idUuid }),
  take: (idUuid) => call('take', { id_uuid: idUuid }),
  /**
   * Aprobar y rechazar avisan al cliente por correo. `notify: false` lo omite
   * —para un duplicado o un registro de prueba— pero el silencio es la
   * excepción: los dos cierran el trámite y sin aviso el cliente se queda
   * esperando una respuesta que ya existe.
   */
  approve: (idUuid, message, notify = true) => call('approve', {
    id_uuid: idUuid, message, notify,
  }),
  reject: (idUuid, message, notify = true) => call('reject', {
    id_uuid: idUuid, message, notify,
  }),
  /**
   * Pide corrección. `items` es { clave: { status, note } } y es lo que de
   * verdad importa: el cliente recibe la lista de conceptos señalados, no un
   * párrafo que tenga que interpretar. `message` es un comentario opcional.
   */
  requestChanges: (idUuid, items, message) => call('request_changes', {
    id_uuid: idUuid, items, message,
  }),
  /**
   * Reabre un expediente ya aprobado o rechazado (vuelve a "En revisión").
   * Sin esto, aprobar y rechazar son irreversibles y un error deja el
   * expediente muerto.
   */
  reopen: (idUuid, message, notify = true) => call('reopen', {
    id_uuid: idUuid, message, notify,
  }),
  /**
   * Registra que operación ya dio de alta al comercio en el sistema de pagos.
   * El alta se hace a mano, fuera del onboarding: esto solo deja constancia de
   * quién y cuándo. Solo para aprobados y una sola vez. No avisa al cliente.
   */
  merchantCreated: (idUuid) => call('merchant_created', { id_uuid: idUuid }),
  /**
   * Reenvía al cliente el correo que le toca por el estado del expediente
   * (corrección, aprobado, rechazo o reapertura). Para cuando el envío
   * original falló o el cliente dice que no le llegó. No cambia el expediente.
   */
  resend: (idUuid) => call('resend', { id_uuid: idUuid }),
  /**
   * Auditoría del expediente: quién hizo qué y cuándo (cliente y revisores).
   * Devuelve { data, total }. Sin el hash de IP.
   */
  audit: (idUuid) => call('audit', { id_uuid: idUuid }),
};

/** Texto accionable a partir del error del backend. */
export function errorText(error) {
  if (error?.response?.data?.message === 'error.items') {
    return 'Marca al menos un concepto como "Corregir" antes de enviar.';
  }
  if (error?.response?.data?.message === 'error.nothing_rejected') {
    return 'No hay nada marcado para corregir.';
  }
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  if (status === 403) return 'No perteneces al grupo de revisores del onboarding.';
  if (status === 401) return 'Tu sesión expiró. Vuelve a entrar al panel.';
  if (message === 'error.transition') {
    const detail = error?.response?.data?.detail;
    if (detail === 'ACTIVE_EXISTS') {
      return 'Este cliente ya tiene otro expediente en curso. Resuélvelo antes de reabrir este.';
    }
    if (detail === 'ALREADY_MARKED') {
      const by = error?.response?.data?.merchant_created_by;
      return by ? `El alta ya estaba registrada por ${by}.` : 'El alta ya estaba registrada.';
    }
    // La base todavía no tiene la operación (falta aplicar su script SQL).
    if (detail === 'BAD_OPTION') return 'Esta acción todavía no está disponible.';
    if (detail === 'NOT_FOUND') return 'No se encontró el expediente.';
    return detail === 'BAD_TRANSITION'
      ? 'El expediente ya no está en un estado que permita esta acción.'
      : 'No se pudo aplicar la acción.';
  }
  if (message === 'error.message') return 'Escribe un mensaje de al menos 10 caracteres.';
  if (message === 'error.tenant_required') return 'Elige una marca para ver su cola.';
  if (message === 'error.key') return 'Ese documento no pertenece a este expediente.';
  // Ver un documento queda registrado: si el registro falla, no se entrega.
  if (message === 'error.audit') return 'No se pudo registrar la consulta del documento. Intenta de nuevo.';
  // La CLABE guardada se cifró para otro expediente (o sin dueño): no se muestra.
  if (message === 'error.clabe_owner') {
    return 'La CLABE guardada no corresponde a este expediente. Márcala para corregir y que el cliente la capture de nuevo.';
  }
  if (message === 'error.nothing_to_resend') return 'En este estado no hay correo que reenviar al cliente.';
  if (message === 'error.mail') return 'No se pudo enviar el correo. Intenta de nuevo en unos minutos.';
  if (status === 404) return 'No se encontró el expediente.';
  return 'Ocurrió un error al consultar el onboarding.';
}

export default Onboarding;
